import { authInterceptor } from './auth-interceptor';
import { ApiError } from './api-error';

interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  data?: any;
  headers?: Record<string, string>;
}

/**
 * Enterprise API request handler with authentication interceptor
 * Provides automatic token refresh, request queuing, and circuit breaker
 */
export async function apiRequest<T = any>(
  url: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const { method = 'GET', data, headers = {} } = options;

  // Configure request
  const config: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers
    },
    body: data ? JSON.stringify(data) : undefined
  };

  try {
    // Use authentication interceptor for all requests
    const response = await authInterceptor.fetch(url, config);

    // Rate-limit metadata survives even when a proxy returns HTML or no body.
    if (response.status === 429) {
      throw new ApiError(response.status, response.headers.get('Retry-After'));
    }

    // Handle non-JSON responses
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error('Invalid response format');
    }

    // Parse response
    const responseData = await response.json();
    
    // Handle API errors
    if (!response.ok) {
      throw new ApiError(response.status, response.headers.get('Retry-After'), responseData.error || responseData.message);
    }

    return responseData;
  } catch (error) {
    // URLs and response errors can contain private values; keep diagnostics static.
    console.error('API request failed');
    
    throw error;
  }
}

/**
 * Convenience methods for common HTTP verbs
 */
export const api = {
  get: <T = any>(url: string, headers?: Record<string, string>) => 
    apiRequest<T>(url, { method: 'GET', headers }),
    
  post: <T = any>(url: string, data?: any, headers?: Record<string, string>) => 
    apiRequest<T>(url, { method: 'POST', data, headers }),
    
  put: <T = any>(url: string, data?: any, headers?: Record<string, string>) => 
    apiRequest<T>(url, { method: 'PUT', data, headers }),
    
  delete: <T = any>(url: string, headers?: Record<string, string>) => 
    apiRequest<T>(url, { method: 'DELETE', headers }),
    
  patch: <T = any>(url: string, data?: any, headers?: Record<string, string>) => 
    apiRequest<T>(url, { method: 'PATCH', data, headers })
};

// Listen for logout events from the interceptor
authInterceptor.on('logout-required', () => {
  // Clear any local state
  sessionStorage.clear();
  
  // Redirect to login
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
});

// Monitor circuit breaker events
authInterceptor.on('circuit-open', (data: any) => {
  console.error('Authentication circuit breaker opened:', data);
  // Could show a user notification here
});

authInterceptor.on('circuit-closed', () => {
  console.log('Authentication service recovered');
});
